/**
 * Normalise any axios error into a string suitable for direct rendering
 * in toast.error / setError / inline UI.
 *
 * Why this exists: FastAPI's RequestValidationError (422) returns
 *   { detail: [{ type, loc, msg, input, url }, ...] }
 * If a caller passes that array straight to a React renderer (or to
 * sonner's toast.error which forwards children to React), React throws
 * #31 "Objects are not valid as a React child", the ErrorBoundary fires,
 * and the page goes blank. This helper guarantees a string.
 *
 * Branches:
 *   - No response (network error, timeout, CORS, abort) → friendly string
 *   - String detail (most FastAPI HTTPException cases) → returned as-is
 *   - Array detail (FastAPI 422 validation errors) → joined "field: msg"
 *   - Object detail (custom shapes) → message field, or JSON.stringify
 *   - Anything else → fallback
 */
export function formatApiError(error, fallback = "Something went wrong") {
  // No response (network error, timeout, CORS)
  if (!error?.response) {
    if (error?.code === "ECONNABORTED") return "Request timed out. Please try again.";
    return error?.message || fallback;
  }
  const detail = error.response.data?.detail;
  // String detail (most FastAPI HTTPException cases)
  if (typeof detail === "string") return detail;
  // Array of validation errors (FastAPI 422)
  if (Array.isArray(detail)) {
    const messages = detail
      .map(e => {
        if (typeof e === "string") return e;
        if (e?.msg) {
          const field = Array.isArray(e.loc) ? e.loc.filter(p => p !== "body").join(".") : "";
          return field ? `${field}: ${e.msg}` : e.msg;
        }
        return null;
      })
      .filter(Boolean);
    return messages.length ? messages.join("; ") : fallback;
  }
  // Object detail (some custom shapes)
  if (detail && typeof detail === "object") {
    if (detail.message) return detail.message;
    try { return JSON.stringify(detail); } catch { return fallback; }
  }
  return fallback;
}
